import { Link } from 'react-router-dom';
import {
  CreditCard,
  Facebook,
  Instagram,
  Lock,
  Mail,
  MessageCircle,
  Phone,
  Truck,
  Undo2,
  Users,
  Youtube,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { usePlatform } from '@/providers/PlatformProvider';
import { PRIMARY_LINKS, visibleLinks } from '@/config/navigation';

/**
 * Site footer — BUILD-PLAN §22 (managed content), §48 (configurable social),
 * §44 (legal/policy centre links).
 *
 * ## What a footer is actually for
 *
 * This was four columns of links and a copyright line, which treats the
 * footer as a legal obligation. On a shop it is the last thing a hesitating
 * customer reads, and it has to answer the questions that stop a purchase:
 * can I pay the way I want, is my card safe, what if it is wrong, how do I
 * reach a human. Hence the reassurance strip and the "We accept" row — those
 * are not decoration, they are the objection-handling part of the page.
 *
 * **Everything here is real.** Social links and contact details come from
 * platform configuration and unconfigured ones are absent rather than dead
 * (§86). Payment methods come from the live provider registry, so the footer
 * can never advertise a method the checkout would then refuse — a trust mark
 * that lies is worse than no trust mark at all.
 */

const SOCIAL_META: Record<string, { label: string; icon: LucideIcon }> = {
  instagram: { label: 'Instagram', icon: Instagram },
  facebook: { label: 'Facebook', icon: Facebook },
  youtube: { label: 'YouTube', icon: Youtube },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle },
  // TikTok and Pinterest have no lucide glyph; they still render, correctly
  // labelled, with a neutral mark rather than being silently dropped.
  tiktok: { label: 'TikTok', icon: MessageCircle },
  pinterest: { label: 'Pinterest', icon: MessageCircle },
};

const POLICY_LINKS = [
  { to: '/policies/terms', label: 'Terms & Conditions' },
  { to: '/policies/privacy', label: 'Privacy Policy' },
  { to: '/policies/cookies', label: 'Cookie Policy' },
  { to: '/policies/shipping', label: 'Shipping & Delivery' },
  { to: '/policies/returns', label: 'Returns & Refunds' },
];

/** Only routes that exist — a footer full of 404s is worse than a short one. */
const HELP_LINKS = [
  { to: '/faq', label: 'Support centre & FAQ' },
  { to: '/track', label: 'Track your order' },
  { to: '/contact', label: 'Contact us' },
  // Shipping and returns are NOT repeated here: they are policy documents and
  // the Legal column owns them. Listing the same page twice under two
  // different names makes a footer feel padded rather than thorough.
];

const COMPANY_LINKS = [
  { to: '/about', label: 'About Glanz Luxe' },
  { to: '/journal', label: 'The Journal' },
  { to: '/corporate-gifting', label: 'Corporate gifting' },
  { to: '/gift-cards', label: 'Gift cards' },
  { to: '/gift-registry', label: 'Gift registry' },
];

/**
 * The reassurance strip.
 *
 * Four statements a customer weighs before committing. Deliberately free of
 * numbers — none of them promises a delivery window or a returns period that
 * the operations side has not agreed to.
 */
const ASSURANCES: Array<{ icon: LucideIcon; title: string; body: string }> = [
  { icon: Truck, title: 'Tracked delivery', body: 'On every order, worldwide.' },
  { icon: Undo2, title: 'Straightforward returns', body: 'Unused, unpersonalised pieces.' },
  { icon: Lock, title: 'Secure checkout', body: 'Card details never touch our servers.' },
  { icon: Users, title: 'Real people', body: 'Talk to us before or after you buy.' },
];

export function Footer() {
  const { config, isVisible } = usePlatform();
  const { social, contact, brand, payments = [] } = config;

  // The same destinations the header and mobile menu use, filtered by the
  // same feature flags — a footer that lists a switched-off section is worse
  // than one that omits it.
  /*
   | Explore, minus anything another column already lists.
   |
   | The footer was repeating itself: "About" and "Contact" appeared here AND
   | again under Company and Help. Deriving the exclusion from the other
   | columns rather than re-typing a trimmed copy of the nav means the two
   | can never disagree — add a destination to the primary nav and it shows up
   | here automatically unless one of the other columns already covers it.
   */
  const coveredElsewhere = new Set(
    [...COMPANY_LINKS, ...HELP_LINKS, ...POLICY_LINKS].map((link) => link.to),
  );

  const exploreLinks = visibleLinks(PRIMARY_LINKS, isVisible).filter(
    (link) => !coveredElsewhere.has(link.to),
  );

  const socialEntries = Object.entries(social).filter(
    (entry): entry is [string, string] => Boolean(entry[1]),
  );

  return (
    <footer className="mt-20 border-t border-line bg-surface-muted">
      {/* ------------------------------------------------- reassurance */}
      <div className="border-b border-line">
        <ul className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-7 px-5 py-8 sm:px-6 lg:grid-cols-4 lg:px-8">
          {ASSURANCES.map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex items-start gap-3">
              <Icon
                className="mt-0.5 h-5 w-5 shrink-0 text-accent"
                strokeWidth={1.5}
                aria-hidden="true"
              />
              <div>
                <p className="text-sm text-ink">{title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted">{body}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ------------------------------------------------------ links */}
      <div className="mx-auto max-w-7xl px-5 py-12 sm:px-6 lg:px-8">
        {/*
          Two columns on a phone, not one.
          ------------------------------------------------------------------
          Stacked single-column, this footer became a long ladder of loose
          links that took several thumb-flicks to pass and gave the eye no
          structure at all. Pairing the link groups side by side halves the
          height and makes the groups read as groups.
        */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3 md:gap-10 lg:grid-cols-5">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Logo size="sm" decorative />

            <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted">
              {brand.description}
            </p>

            <p className="mt-6 font-display text-base italic text-accent">{brand.tagline}</p>
          </div>

          <FooterNav id="footer-explore" heading="Explore" links={exploreLinks} />
          <FooterNav id="footer-company" heading="Company" links={COMPANY_LINKS} />
          <FooterNav id="footer-help" heading="Help" links={HELP_LINKS} />
          <FooterNav id="footer-legal" heading="Legal" links={POLICY_LINKS} />
        </div>

        {/* ---------------------------------------- contact + social */}
        <div className="mt-12 flex flex-col gap-8 border-t border-line pt-10 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="h-px w-5 bg-accent/50" aria-hidden="true" />
              <h2 className="text-[0.6875rem] uppercase tracking-[0.2em] text-ink">Contact</h2>
            </div>

            <ul className="mt-4 flex flex-col gap-2.5 text-sm text-muted">
              {contact['email'] && (
                <li>
                  <a
                    href={`mailto:${contact['email']}`}
                    className="inline-flex items-center gap-2.5 transition-colors hover:text-accent"
                  >
                    <Mail className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                    {contact['email']}
                  </a>
                </li>
              )}

              {contact['phone'] && (
                <li>
                  <a
                    href={`tel:${contact['phone']}`}
                    className="inline-flex items-center gap-2.5 transition-colors hover:text-accent"
                  >
                    <Phone className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                    {contact['phone']}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {socialEntries.length > 0 && (
            <div>
              <div className="flex items-center gap-2.5">
                <span className="h-px w-5 bg-accent/50" aria-hidden="true" />
                <h2 className="text-[0.6875rem] uppercase tracking-[0.2em] text-ink">Connect with us</h2>
              </div>

              <ul className="mt-4 flex flex-wrap gap-2.5">
                {socialEntries.map(([key, url]) => {
                  const meta = SOCIAL_META[key];
                  const Icon = meta?.icon ?? MessageCircle;

                  return (
                    <li key={key}>
                      <a
                        href={url}
                        target="_blank"
                        // noreferrer also implies noopener; both guard against
                        // reverse tabnabbing on external links.
                        rel="noreferrer noopener"
                        aria-label={meta?.label ?? key}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-line text-muted transition-colors hover:border-accent hover:bg-accent hover:text-white"
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/*
            Only the methods the store can genuinely take, from the live
            registry — see the note at the top of this file.
          */}
          {payments.length > 0 && (
            <div>
              <div className="flex items-center gap-2.5">
                <span className="h-px w-5 bg-accent/50" aria-hidden="true" />
                <h2 className="text-[0.6875rem] uppercase tracking-[0.2em] text-ink">We accept</h2>
              </div>

              <ul className="mt-4 flex flex-wrap gap-2">
                {payments.map((method) => (
                  <li
                    key={method.key}
                    className="inline-flex min-h-9 items-center gap-2 rounded-sm border border-line bg-surface px-3 text-xs text-muted"
                  >
                    <CreditCard
                      className="h-3.5 w-3.5 text-accent"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                    {method.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ----------------------------------------------------- legal */}
      <div className="border-t border-line">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-6 text-xs text-muted sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <p>
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </p>

          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {POLICY_LINKS.slice(0, 3).map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="underline-offset-4 transition-colors hover:text-accent hover:underline"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}

/**
 * One column of links.
 *
 * Extracted because there are now four of them and they had begun to differ —
 * one used `space-y-3`, another `gap-2` — which is exactly how a footer ends
 * up looking slightly wrong without any single thing being wrong.
 */
function FooterNav({
  id,
  heading,
  links,
}: {
  id: string;
  heading: string;
  links: ReadonlyArray<{ to: string; label: string }>;
}) {
  if (links.length === 0) {
    return null;
  }

  return (
    <nav aria-labelledby={id}>
      {/* The rule beside the label is the site's signature mark — it appears
          on every homepage section, every page header and the product page.
          A footer that omits it is the one place that looks bolted on. */}
      <div className="flex items-center gap-2.5">
        <span className="h-px w-5 bg-accent/50" aria-hidden="true" />
        <h2 id={id} className="text-[0.6875rem] uppercase tracking-[0.2em] text-ink">
          {heading}
        </h2>
      </div>

      <ul className="mt-5 flex flex-col gap-3">
        {links.map(({ to, label }) => (
          <li key={to}>
            <Link
              to={to}
              /* An underline that grows from the left on hover, rather than a
                 colour change alone — the same motion the product card title
                 uses, so the whole site agrees on what "this is a link" feels
                 like. */
              className="group inline-flex text-sm text-muted transition-colors hover:text-accent"
            >
              <span className="bg-linear-to-r from-accent to-accent bg-size-[0%_1px] bg-bottom-left bg-no-repeat pb-0.5 transition-[background-size] duration-300 group-hover:bg-size-[100%_1px] motion-reduce:transition-none">
                {label}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
